import { SendRequestAccessBody } from '@ardoq/api';
import {
  APIPresentationAssetAttributes,
  ArdoqId,
  Asset,
  AssetType,
  PermissionAccessLevel,
  ResourceType,
  SortOrder,
  AssetFolder as StreamFolder,
  Workspace as StreamWorkspace,
} from '@ardoq/api-types';
import { findMatchGroups } from '@ardoq/common-helpers';
import { Locale } from '@ardoq/locale';
import { smartSort } from '@ardoq/pagination';
import { findRangeSelectIds, GenericRow, RowType } from '@ardoq/table-legacy';
import { Selected } from 'aqTypes';
import { AssetRow, SurveyRow } from 'components/AssetsBrowser/types';
import { uniq, without } from 'lodash';
import { useState } from 'react';
import * as selectAllUtils from './SelectAllCheckbox';
import { DatasourceItem, Folder, Row, RowItem } from './types';
import { t, type I18nLocale } from '@ardoq/i18n';

export const includesAll = selectAllUtils.includesAll;
export const includesAny = selectAllUtils.includesAny;

type ById<T> = { [key: string]: T };
type WithMeta<T> = T & { meta: { [key: string]: any } };
type WithRowType<T> = T & { rowType: RowType };
type AssetStream =
  | StreamWorkspace
  | APIPresentationAssetAttributes
  | StreamFolder;
type GroupedFolders = ById<StreamFolder[]>;

const groupByFolderId = <
  T extends { _id: string; meta?: { folderId?: string | null } },
>(
  list: T[],
  mapFn: (item: T) => WithRowType<T>
): ById<WithRowType<T>[]> => {
  const result: ById<WithRowType<T>[]> = { ROOT: [] };
  for (const item of list) {
    const key = item.meta?.folderId || 'ROOT';
    const mapped = mapFn(item);
    if (result[key]) {
      result[key].push(mapped);
    } else {
      result[key] = [mapped];
    }
  }
  return result;
};

const createFolderTree = (
  currentFolder: WithMeta<StreamFolder>,
  foldersHash: GroupedFolders,
  rowGroups: ById<WithRowType<AssetStreamOrAsset>[]>[]
) => {
  const currentFolderId = currentFolder._id;
  const subfolders = foldersHash[currentFolderId] || [];
  const subRowGroups = rowGroups.flatMap(
    rowGroup => rowGroup[currentFolderId] || []
  );
  const subfoldersWithTree: StreamFolder[] = subfolders.map(subfolder =>
    createFolderTree(subfolder, foldersHash, rowGroups)
  );
  const children = [...subfoldersWithTree, ...subRowGroups.flat()];

  return {
    ...currentFolder,
    children: children.length
      ? children
      : [
          {
            rowType: RowType.EMPTY_FOLDER,
            meta: {
              folderId: currentFolder._id,
              path: currentFolder.meta.path,
              level: currentFolder.meta.level,
            },
            contentByType: currentFolder.contentByType,
          },
        ],
  };
};

const assetTypeToRowType: Record<string, RowType> = {
  [AssetType.FOLDER]: RowType.FOLDER,
  [AssetType.PRESENTATION]: RowType.PRESENTATION,
  [AssetType.METAMODEL]: RowType.METAMODEL,
  [AssetType.SURVEY]: RowType.SURVEY,
  [AssetType.SCENARIO]: RowType.SCENARIO,
  [AssetType.REPORT]: RowType.REPORT,
  [AssetType.DASHBOARD]: RowType.DASHBOARD,
  [AssetType.TRAVERSAL]: RowType.TRAVERSAL,
  [AssetType.BOOKMARK]: RowType.BOOKMARK,
  [AssetType.VIEWPOINT]: RowType.VIEWPOINT,
  [AssetType.BROADCAST]: RowType.BROADCAST,
};

export const getRowTypeByAssetType = (
  assetType: AssetType,
  isExternallyManaged: boolean
): RowType => {
  if (assetType === AssetType.WORKSPACE) {
    return isExternallyManaged ? RowType.MANAGED_WORKSPACE : RowType.WORKSPACE;
  }
  return assetTypeToRowType[assetType] ?? RowType.EMPTY_FOLDER;
};

type AssetStreamOrAsset = AssetStream | Asset;

const addRowType = <T extends AssetStreamOrAsset>(
  asset: T
): T & { rowType: RowType } => {
  const { meta } = asset as WithMeta<T>;
  const result = asset as T & { rowType: RowType };
  result.rowType = getRowTypeByAssetType(
    meta.assetType,
    meta.isExternallyManaged ?? false
  );
  return result;
};

const assetGroupToRowGroup = <
  T extends AssetStreamOrAsset = AssetStreamOrAsset,
>(
  assetGroup: T[]
): ById<WithRowType<T>[]> => groupByFolderId(assetGroup, addRowType);

const assetsToRowGroup = (
  assets: AssetStreamOrAsset[]
): ById<WithRowType<AssetStreamOrAsset>[]> =>
  groupByFolderId(assets, addRowType);

export const combineFolders = (
  folders: StreamFolder[],
  assetsGroups: AssetStreamOrAsset[][]
) => {
  const rowGroups = assetsGroups.map(assetGroupToRowGroup);
  const groupedFolders = assetGroupToRowGroup(folders);
  const rootfoldersWithTree = (groupedFolders.ROOT || []).map(subfolder =>
    createFolderTree(subfolder, groupedFolders, rowGroups)
  );

  return [
    ...rootfoldersWithTree,
    ...rowGroups.flatMap(rowGroup => rowGroup.ROOT || []),
  ] as unknown as Row<AssetRow>[];
};

const removeMatchGroups = <T extends GenericRow>(dataSource: T[]) =>
  dataSource.map(row =>
    row?.meta?.matchGroups
      ? { ...row, meta: { ...row.meta, matchGroups: undefined } }
      : row
  );

export const combineFoldersFromAssetArray = (
  folders: StreamFolder[],
  assets: Asset[]
) => {
  const rowGroups = assetsToRowGroup(assets);
  const groupedFolders = assetGroupToRowGroup<StreamFolder>(folders);
  const rootfoldersWithTree = (groupedFolders.ROOT || []).map(subfolder =>
    createFolderTree(subfolder, groupedFolders, [rowGroups])
  );

  const rootAssets = rowGroups.ROOT || [];
  return [...rootfoldersWithTree, ...rootAssets] as Row<AssetRow>[];
};

const addDontExpandOnSearch = (row: GenericRow): GenericRow => {
  if (row.rowType === RowType.FOLDER) {
    return {
      ...row,
      meta: { ...row.meta, dontExpandOnSearch: true },
    };
  }
  return row;
};

const mergeMatchedAndUnmatchedChildren = (
  matched: GenericRow[],
  unmatched: GenericRow[]
): GenericRow[] => {
  const matchedIds = matched.map(row => row._id);
  return [
    ...matched,
    ...removeMatchGroups(unmatched)
      .filter(row => !matchedIds.includes(row._id))
      .map(addDontExpandOnSearch),
  ];
};

const applyMatchGroups = <T extends GenericRow>(
  dataSource: T[],
  searchPhrase: string,
  locale: Locale
) =>
  dataSource.reduce((acc: T[], row) => {
    if (!row.name) return acc;
    const matchGroups = findMatchGroups(row.name, searchPhrase, locale);
    const matchedChildren = applyMatchGroups(
      row.children ?? [],
      searchPhrase,
      locale
    );
    const rowWithMatchGroups = {
      ...row,
      meta: { ...row.meta, matchGroups },
    };

    if (matchGroups.match) {
      if (row.rowType === RowType.FOLDER) {
        rowWithMatchGroups.children = mergeMatchedAndUnmatchedChildren(
          matchedChildren,
          row.children || []
        );
      }
      acc.push(rowWithMatchGroups);
    } else if (matchedChildren.length) {
      rowWithMatchGroups.children = matchedChildren;
      acc.push(rowWithMatchGroups);
    }

    return acc;
  }, []);

export const searchDatasourceByName = <T extends GenericRow>(
  dataSource: T[],
  searchPhrase: string,
  locale: Locale
) => {
  if (!searchPhrase) return removeMatchGroups(dataSource);

  return applyMatchGroups(dataSource, searchPhrase, locale);
};

const getNextSortOrder = (
  sortOrder: SortOrder,
  skipDefaultSortOrder?: boolean
): SortOrder => {
  if (sortOrder === SortOrder.DEFAULT) return SortOrder.ASC;
  if (sortOrder === SortOrder.ASC) return SortOrder.DESC;
  // if DESC
  return skipDefaultSortOrder ? SortOrder.ASC : SortOrder.DEFAULT;
};

type UseSortingProps = {
  sortOrder?: SortOrder;
  sortByField?: string | null;
  skipDefaultSortOrder?: boolean;
  onSortChange?: (sortOrder: SortOrder, sortByField: string | null) => void;
};

export const useSorting = (
  props: UseSortingProps = {}
): [SortOrder, string | null, (sortByField: string) => void] => {
  const onSortChange = props.onSortChange || (() => {});
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    props.sortOrder || SortOrder.DEFAULT
  );
  const [sortByField, setSortByField] = useState<string | null>(
    props.sortByField || null
  );

  const setSortByFieldFn = (newSortByField: string) => {
    if (sortByField === newSortByField) {
      const newSortOrder = getNextSortOrder(
        sortOrder,
        props.skipDefaultSortOrder
      );
      setSortOrder(newSortOrder);
      if (newSortOrder === SortOrder.DEFAULT) setSortByField(null);
      onSortChange(
        newSortOrder,
        newSortOrder === SortOrder.DEFAULT ? null : newSortByField
      );
    } else {
      setSortOrder(SortOrder.ASC);
      setSortByField(newSortByField);
      onSortChange(SortOrder.ASC, newSortByField);
    }
  };

  return [sortOrder, sortByField, setSortByFieldFn];
};

const toggle = (item: string, source: string[]) =>
  source.includes(item) ? without(source, item) : [...source, item];

const isAsset = (row: Row<any>): row is AssetRow =>
  [
    RowType.WORKSPACE,
    RowType.TRAVERSAL,
    RowType.MANAGED_WORKSPACE,
    RowType.PRESENTATION,
    RowType.METAMODEL,
    RowType.SURVEY,
    RowType.SCENARIO,
    RowType.REPORT,
    RowType.DASHBOARD,
    RowType.DOCUMENT,
    RowType.IMAGE,
    RowType.UNKOWN_FILE,
    RowType.TRAVERSAL,
    RowType.VIEWPOINT,
    RowType.BROADCAST,
    RowType.BOOKMARK,
  ].includes(row.rowType);

export const isAssetFolder = <T extends RowItem>(
  row: Row<T>
): row is Folder<T> =>
  row.rowType === RowType.FOLDER && row.meta.assetType === AssetType.FOLDER;

export const isFolder = <T extends RowItem>(
  row: Row<T>
): row is T & DatasourceItem<RowType.FOLDER> => row.rowType === RowType.FOLDER;

export const isNotFolder = <T extends RowItem>(row: Row<T>): row is T =>
  ![RowType.FOLDER, RowType.EMPTY_FOLDER].includes(row.rowType);

export const getLastModifiedByImage = <T extends RowItem>(
  row: Row<T>
): string | undefined =>
  'lastModifiedByImage' in row.meta ? row.meta.lastModifiedByImage : undefined;

const isEmptyFolder = (row: RowItem | Folder<RowItem>) =>
  isAssetFolder(row) && row.content.length === 0;

const isRowSelectable = (row: RowItem | Folder<RowItem>) =>
  isAsset(row) || isEmptyFolder(row);

export const getSelectableIdsFromDataSource = (dataSource: any[]): string[] => {
  return dataSource.flatMap(dataSourceRow => {
    if (isRowSelectable(dataSourceRow)) {
      return dataSourceRow._id;
    } else if (isFolder(dataSourceRow)) {
      return getSelectableIdsFromDataSource(dataSourceRow.children || []);
    }
    return [];
  });
};

export const extractAssetsIdsList = (dataSource: any[]): string[] =>
  dataSource.reduce((acc: string[], item) => {
    if (isRowSelectable(item)) {
      acc.push(item._id);
    } else if (isFolder(item)) {
      return [...acc, ...extractAssetsIdsList(item.children || [])];
    }
    return acc;
  }, []);

export const getSelectRowsFn =
  (
    setSelected: (selected: string[]) => void,
    selected: string[],
    dataSource: any[]
  ) =>
  (row: RowItem | Folder<RowItem>, rangeSelect = false) => {
    if (rangeSelect) {
      const dataSourceIds = extractAssetsIdsList(dataSource);
      const rowIds = extractAssetsIdsList([row]);
      const rangeIds = rowIds.flatMap(id =>
        findRangeSelectIds(id, selected, dataSourceIds)
      );
      setSelected(uniq(rangeIds));
    } else if (isRowSelectable(row)) {
      setSelected(toggle(row._id, selected));
    } else if (isFolder(row)) {
      const assetsIds = extractAssetsIdsList([row]);
      const updatedSelectedAssets = includesAny(selected, assetsIds)
        ? without(selected, ...assetsIds)
        : uniq([...selected, ...assetsIds]);
      setSelected(updatedSelectedAssets);
    }
  };

const extractFoldersAndAssets = (dataSource: any[]) =>
  dataSource.reduce(
    ([folders, assets]: any[], dataSourceRow: any) => {
      if (isFolder(dataSourceRow)) {
        folders.push(dataSourceRow);
      } else {
        assets.push(dataSourceRow);
      }

      return [folders, assets];
    },
    [[], []]
  );

const sortFolders = (
  folders: Folder<RowItem>[],
  sortByField: string | null,
  sortOrder: SortOrder
) =>
  smartSort(folders, sortByField, sortOrder).map(dataSourceRow => {
    if (dataSourceRow.children)
      return {
        ...dataSourceRow,
        children: sortDataSource(
          dataSourceRow.children,
          sortByField,
          sortOrder
        ),
      };
    return dataSourceRow;
  });

export const sortDataSource = (
  dataSource: any[],
  sortByField: string | null,
  sortOrder: SortOrder
): any[] => {
  // prevent trimming empty folders rows
  if (
    dataSource.length === 1 &&
    dataSource[0].rowType === RowType.EMPTY_FOLDER
  ) {
    return dataSource;
  }
  const [folders, assets] = extractFoldersAndAssets(dataSource);

  return [
    ...sortFolders(folders, sortByField, sortOrder),
    ...smartSort(assets, sortByField, sortOrder),
  ];
};

export const findIsSelected = (
  row: RowItem | Folder<RowItem>,
  selectedAssets: string[]
): Selected => computeSelectedStatus(row, new Set(selectedAssets));

export const computeSelectedStatus = (
  row: RowItem | Folder<RowItem>,
  selectedAssets: Set<ArdoqId>
): Selected => {
  if (isRowSelectable(row)) {
    return selectedAssets.has(row._id)
      ? Selected.SELECTED
      : Selected.NOT_SELECTED;
  }

  if (isAssetFolder(row)) {
    const selectableDescendantIds = new Set(
      getSelectableIdsFromDataSource([row])
    );
    const selectedDescendantIds =
      selectableDescendantIds.intersection(selectedAssets);
    if (selectedDescendantIds.size === 0) {
      return Selected.NOT_SELECTED;
    }
    if (selectedDescendantIds.size === selectableDescendantIds.size)
      return Selected.SELECTED;
    return Selected.MIXED;
  }

  return Selected.NOT_SELECTED;
};

export const getRequestSurveyWorkspaceAccessBody = (
  survey: SurveyRow,
  workspaceId: ArdoqId,
  user: { name?: string | null; email?: string | null },
  i18nLocale: I18nLocale
): SendRequestAccessBody => {
  const userName =
    user.name ??
    t(i18nLocale, 'entityBrowser:requestAccess.theUser', 'The user');
  const userSuffix = user.email ? ` (${user.email})` : '';
  return {
    explanation: t(
      i18nLocale,
      'entityBrowser:requestAccess.workspaceAccessExplanation',
      { userName, userSuffix, surveyName: survey.name },
      '{{userName}}{{userSuffix}} administers "{{surveyName}}" but lacks admin access for the selected workspace.'
    ),
    requests: [
      {
        resourceId: workspaceId,
        resourceType: ResourceType.WORKSPACE,
        access: { permission: PermissionAccessLevel.ADMIN },
      },
    ],
  };
};

export const getRequestSurveyAccessBody = (
  survey: SurveyRow,
  user: { name?: string | null; email?: string | null },
  i18nLocale: I18nLocale
): SendRequestAccessBody => {
  const userName =
    user.name ??
    t(i18nLocale, 'entityBrowser:requestAccess.theUser', 'The user');
  const userSuffix = user.email ? ` (${user.email})` : '';
  return {
    explanation: t(
      i18nLocale,
      'entityBrowser:requestAccess.surveyAccessExplanation',
      { userName, userSuffix, surveyName: survey.name },
      '{{userName}}{{userSuffix}} wants to edit the survey "{{surveyName}}" but lacks writing permission.'
    ),
    requests: [
      {
        resourceId: survey._id,
        resourceType: ResourceType.SURVEY,
        access: { permission: PermissionAccessLevel.EDIT },
      },
    ],
  };
};
